import * as Auth from 'aws-amplify/auth';
import * as AuthOverrides from './state/amplify/Amplify.overrides';

import { API_NAME, API_URL } from '#config/runtimeconfig';
/* eslint-disable camelcase */
import { fetchJSON, handleError, handleFetchResponse } from '../common/services/helpers';

// @ts-check
import API from '/b2b/common/helpers/API';
import { Amplify } from 'aws-amplify';
import { updateAuth } from '../amplify.config';

/** @typedef {'SMS'|'TOTP'|'EMAIL'} AmplifyMfaType */

const requiredSignUpProps = ['name', 'email', 'given_name', 'family_name', 'phone_number'];

const toCognitoMap = {
    firstName: /** @type {const} */ ('given_name'),
    middleName: /** @type {const} */ ('middle_name'),
    lastName: /** @type {const} */ ('family_name'),
    phone: /** @type {const} */ ('phone_number'),
};

let AuthSymbol;
function getAuthSymbol({ username } = {}) {
    if (!AuthSymbol && username) {
        AuthSymbol = { description: `${username}-auth` };
    } else if (AuthSymbol) {
        if (username && AuthSymbol.description !== `${username}-auth`) {
            // New username being used
            AuthSymbol = { description: `${username}-auth` };
        }
    } else {
        throw new Error('No username provided for authentication context');
    }
    return AuthSymbol;
}

/** @type {SwitchKeyValue<{ [K in keyof typeof toCognitoMap]: toCognitoMap[K] }}> */
const fromCognitoMap = Object.entries(toCognitoMap).reduce(
    (map, [key, value]) => ((map[value] = key), map),
    {}
);

/** @typedef {Record<keyof toCognitoMap, string> & Record<keyof Omit<Auth.UserAttributeKey, keyof typeof fromCognitoMap>, string>} OsanoUserDetails */

/** @type {() => Promise<Auth.GetCurrentUserOutput>} */
export const getCognitoUser = () => Auth.getCurrentUser();

/**
 * TODO: Make this more strongly typed
 * @param {Partial<Record<Auth.UserAttributeKey, string>>} params The Cognito params to map to Osano standardized user details
 * @param {string[]} [allowedReceived] A specific set of keys to include in the response
 * @returns {Partial<OsanoUserDetails>} The mapped user details
 */
export const mapFromCognitoParams = (params, allowedReceived) => {
    const standardizedProps = Object.entries(params).reduce((props, [key, value]) => {
        if (key === 'address') {
            props[key] = JSON.parse(value);
        } else if (key === 'identities') {
            // Federated identities the user uses
            try {
                const identities = JSON.parse(value);
                props[key] = {};
                identities.forEach(({ providerName, providerType, userId, primary }) => {
                    props[key][providerName] = {
                        primary,
                        type: providerType,
                        userId,
                    };
                });
            } catch (e) {
                // Malformed
            }
        } else if (fromCognitoMap[key]) {
            props[fromCognitoMap[key]] = value;
        } else {
            props[key] = value;
        }
        return props;
    }, {});

    const allowed = allowedReceived || Object.keys(standardizedProps);

    return Object.keys(standardizedProps)
        .filter(
            key =>
                allowed.includes(key) &&
                standardizedProps[key] !== undefined &&
                standardizedProps[key] !== ''
        )
        .reduce((o, key) => ((o[key] = standardizedProps[key]), o), {});
};

export const mapToCognitoParams = (params, allowedProvided) => {
    let {
        firstName,
        lastName,
        policy,
        street_address = '',
        locality = '',
        region = '',
        postal_code = '',
        country = 'US',
    } = params;

    const name =
        `${firstName || ''}${firstName && lastName ? ' ' : ''}${lastName || ''}` || undefined;
    const address =
        street_address && locality && region && postal_code
            ? {
                  formatted: `${street_address}\n${locality}, ${region} ${postal_code}\n${country}`,
                  street_address,
                  locality,
                  region,
                  postal_code,
                  country,
              }
            : undefined;

    const standardizedProps = Object.entries(params).reduce(
        (props, [key, value]) => {
            if (key === 'phone') {
                if (value && value.indexOf('+') !== 0) {
                    props[toCognitoMap[key]] = `+${value}`;
                } else {
                    props[toCognitoMap[key]] = value ? value : '';
                }
            } else if (toCognitoMap[key]) {
                props[toCognitoMap[key]] = value;
            } else {
                props[key] = value;
            }
            return props;
        },
        {
            address: address ? JSON.stringify(address) : address,
            name,
            policy: policy ? policy.toString() : policy,
        }
    );

    const allowed = allowedProvided || Object.keys(standardizedProps);

    return Object.keys(standardizedProps)
        .filter(
            key =>
                allowed.includes(key) &&
                standardizedProps[key] !== undefined &&
                standardizedProps[key] !== ''
        )
        .reduce((o, key) => ((o[key] = standardizedProps[key]), o), {});
};

export const extractUserDetails = (
    /** @type {Awaited<ReturnType<Auth.fetchUserAttributes>>} */ attributes
) => {
    const userDetails = {};
    for (let [key, value] of Object.entries(attributes || {})) {
        if (key.indexOf('custom:') === 0) {
            const customKey = key.split(':').slice(1).join(':');
            userDetails[customKey] = value;
        } else {
            userDetails[key] = value;
        }
    }
    return mapFromCognitoParams(userDetails || {});
};

export const getUserData = () => {
    return Auth.fetchUserAttributes().then(extractUserDetails);
};

const getSignedInUserSession = cognitoUser => cognitoUser?.signInUserSession;

export const getAccessToken = cognitoUser => getSignedInUserSession(cognitoUser)?.accessToken;
export const getAccessTokenExpiration = cognitoUser =>
    getAccessToken(cognitoUser)?.payload?.exp || 0;

export const fixAuthContext = () => {
    return Auth.fetchAuthSession()
        .then(session => {
            // If the session is valid, return it
            if (session && session.identityId) {
                return session;
            }
            throw new Error('No current user');
        })
        .catch(e => {
            const { message = e } = e;
            switch (message) {
                case 'Token is not from a supported provider of this identity pool': // Amplify v6
                case 'No current user': {
                    // User pool may be wrong, lets try the other one
                    const config = Amplify.getConfig();
                    switch (config.Auth.Cognito.userPoolId) {
                        case process.env.COGNITO_IDP_USER_POOL_ID: {
                            // Switch to Cognito User Pool
                            updateAuth({
                                ssoEnabled: false,
                            });
                            break;
                        }

                        default: {
                            // Switch to SSO User Pool
                            updateAuth({
                                ssoEnabled: true,
                            });
                            break;
                        }
                    }
                    // Try again
                    return Auth.fetchAuthSession({ forceRefresh: true });
                }
            }
            throw e;
        });
};

export const refreshTokens = () => {
    return Auth.fetchAuthSession()
        .then(session => {
            if (session && session.identityId) {
                return session;
            }
            return Auth.fetchAuthSession({ forceRefresh: true });
        })
        .catch(e => {
            throw e;
        });
};

export const checkUser = session => {
    const promise = session
        ? Promise.resolve(session)
        : Auth.fetchAuthSession({
              // Do NOT get a cached response
              forceRefresh: true,
          });
    return promise
        .then(session => {
            if (session.identityId) {
                return Auth.getCurrentUser();
            }
            throw 'not authenticated';
        })
        .catch(error => {
            switch (error) {
                case 'not authenticated': {
                    throw {
                        status: 'error',
                        code: 'NotAuthorizedException',
                        name: 'NotAuthorizedException',
                        message: 'User is not authenticated',
                    };
                }
            }
            throw error;
        });
};

export const updateUserAttributes = params => {
    // Only allow updates of specific parameters
    const userAttributes = mapToCognitoParams(params, [
        'name',
        'given_name',
        'family_name',
        'address',
        'phone_number',
    ]);
    return Auth.updateUserAttributes({ userAttributes });
};

export const signUp = params => {
    const { username, password, ...extra } = params;

    return Auth.signUp({
        username,
        password,
        options: {
            userAttributes: mapToCognitoParams(extra, requiredSignUpProps),
        },
    })
        .catch(handleError)
        .then(response => {
            const {
                isSignUpComplete,
                userId: _userId,
                nextStep: _nextStep,
            } = /** @type {Auth.SignUpOutput} */ (response);
            return Auth.getCurrentUser().then(cognitoUser => {
                return extractUserDetails(cognitoUser).then(details => ({
                    details,
                    cognitoUser,
                    userConfirmed: isSignUpComplete,
                }));
            });
        });
};

export const signIn = params => {
    return AuthOverrides.signInHelper(params, getAuthSymbol(params));
};

export const rememberMe = () =>
    Auth.rememberDevice()
        // Cannot remember the device? Not a big deal and can happen for a variety of reasons that are browser related
        .catch(e => {
            switch (e.name) {
                case 'UnsupportedOperationException': {
                    // You cannot remember a device if the UserPool doesn't support remembering
                    break;
                }
                case 'DeviceMetadataNotFoundException': {
                    // Either deviceKey, deviceGroupKey or secretPassword were not found during the sign-in process.
                    break;
                }
                default: {
                    // Handle other errors
                    break;
                }
            }
        });
export const forgetMe = () =>
    Auth.forgetDevice()
        // Cannot forget the device? Maybe it wasn't remembered in the first place.
        .catch(e => {
            switch (e.name) {
                case 'UnsupportedOperationException': {
                    // You cannot forget a device if the UserPool doesn't support remembering
                    break;
                }
            }
        });

export const fetchAmplifyContext = params => {
    const { username } = params;
    return new Promise((resolve, reject) => {
        const url = new URL(
            `${API_URL}/auth/${encodeURIComponent(username)}`,
            window.location.origin
        );
        resolve(fetchJSON(url).catch(reject));
    });
};

/** @param {import('aws-amplify/auth').SignInWithRedirectInput} params */
export const signInFederated = params => {
    return Auth.signInWithRedirect(params).then(Auth.getCurrentUser);
};

export const confirmSignIn = params => {
    return Auth.confirmSignIn(params);
};

export const confirmSignUp = params => {
    const { username, code } = params;
    return Auth.confirmSignUp({ username, confirmationCode: code });
};

export const sendMFASelection = mfaType => {
    return AuthOverrides.selectMfa(mfaType, getAuthSymbol());
};

export const getUserMfaOptions = () => {
    return Auth.fetchMFAPreference().then(mfaOptions => {
        if (!mfaOptions?.enabled?.length) {
            // No MFA options enabled only happens when it's a new user, so lets force them
            throw new Error('No MFA options enabled');
        }
        return mfaOptions;
    });
};

export const verifyEmail = params => {
    const { code } = params;
    return Auth.confirmUserAttribute({ userAttributeKey: 'email', confirmationCode: code });
};

export const verifyPhone = params => {
    const { code } = params;
    return Auth.confirmUserAttribute({
        userAttributeKey: 'phone_number',
        confirmationCode: code,
    });
};

export const verifyTotpToken = ({ code }) =>
    Auth.verifyTOTPSetup({ code })
        .then(() => {
            return Auth.getCurrentUser();
        })
        .catch(handleError);

export const addMFA = async (/** @type {keyof MFA_TYPES} */ mfaType, setAsPreferred = false) => {
    let mfaOptions = { enabled: [] };
    if (setAsPreferred) {
        mfaOptions.preferred = mfaType.toLowerCase();
    }
    try {
        mfaOptions = await getUserMfaOptions();
    } catch {
        // User had no MFA options
    }
    const { preferred, enabled } = mfaOptions;
    /** @type {Promise<Auth.UpdateMFAPreferenceInput>} */
    const updatedMfaPreferences = enabled.reduce(
        (acc, curr) => {
            if (!acc[curr.toLowerCase()]) {
                acc[curr.toLowerCase()] = 'ENABLED';
            }
            return acc;
        },
        /** @type {Auth.UpdateMFAPreferenceInput} */ (
            setAsPreferred
                ? {
                      [mfaType.toLowerCase()]: 'PREFERRED',
                  }
                : {
                      ...(preferred ? { [preferred.toLowerCase()]: 'PREFERRED' } : {}),
                      [mfaType.toLowerCase()]: 'ENABLED',
                  }
        )
    );
    if (Object.keys(updatedMfaPreferences).length > 1 && !setAsPreferred) {
        // User has multiple MFA options, so we should make sure none are preferred unless we specified
        for (const key of Object.keys(updatedMfaPreferences)) {
            if (updatedMfaPreferences[key] === 'PREFERRED') {
                updatedMfaPreferences[key] = 'ENABLED';
            }
        }
    }
    try {
        await Auth.updateMFAPreference(updatedMfaPreferences);
    } catch (error) {
        handleError(error);
    }

    return updatedMfaPreferences;
};

export const removeMFA = async (/** @type {keyof MFA_TYPES} */ mfaType) => {
    let mfaOptions = { preferred: mfaType.toLowerCase(), enabled: [] };
    try {
        mfaOptions = await getUserMfaOptions();
    } catch {
        // User had no MFA options
    }
    const { preferred, enabled } = mfaOptions;
    /** @type {Promise<Auth.UpdateMFAPreferenceInput>} */
    const updatedMfaPreferences = enabled.reduce(
        (acc, curr) => {
            acc[curr.toLowerCase()] =
                curr.toLowerCase() === mfaType.toLowerCase() ? 'DISABLED' : 'ENABLED';
            return acc;
        },
        /** @type {Auth.UpdateMFAPreferenceInput} */ (
            preferred && preferred.toLowerCase() !== mfaType.toLowerCase()
                ? {
                      [preferred.toLowerCase()]: 'PREFERRED',
                  }
                : {}
        )
    );
    // We need to explicitly set the MFA type to be removed
    updatedMfaPreferences[mfaType.toLowerCase()] = 'DISABLED';
    try {
        await Auth.updateMFAPreference(updatedMfaPreferences);
    } catch (error) {
        handleError(error);
    }

    return updatedMfaPreferences;
};

export const setupTOTP = () => Auth.setUpTOTP();

export const resendSignUpCode = params => {
    const { username } = params;
    return Auth.resendSignUpCode({ username });
};

export const resendEmailVerificationCode = () => {
    return Auth.sendUserAttributeVerificationCode({ userAttributeKey: 'email' });
};

export const resendPhoneVerificationCode = () => {
    return Auth.sendUserAttributeVerificationCode({ userAttributeKey: 'phone_number' });
};

export const resendSmsOtp = async () => {
    /**
     *  The Amplify SDK currently does not support resending the SMS code once it's been sent.
     *  As a workaround, you can sign the user out, then sign them back in, and select the desired MFA type again if necessary.
     */
    try {
        await Auth.signOut();
    } catch (error) {
        // Might not actually be logged in anymore...waited too long
    }
    const signInOutput = await signIn({});
    switch (signInOutput?.nextStep.signInStep) {
        case 'CONFIRM_SIGN_IN_WITH_SMS_CODE':
            // User has SMS preferred, or it's the only MFA option. Code was immediately sent
            break;
        case 'CONFIRM_SIGN_IN_WITH_MFA_SELECTION':
            // User has multiple MFA methods, so we re-select SMS
            return Auth.confirmSignIn({ challengeResponse: 'SMS' });
        default:
            // Auth state is unexpected
            break;
    }

    return signInOutput;
};

export const resetTemporaryPassword = email => {
    /** @type {RequestInit} */
    const options = {
        method: 'POST',
        mode: 'cors',
        cache: 'no-cache',
        redirect: 'follow',
        referrer: 'no-referrer',
        referrerPolicy: 'no-referrer',
        body: JSON.stringify({ email }),
        credentials: 'same-origin',
        headers: {
            'Content-Type': 'application/json',
        },
    };
    return fetch(`${API_URL}/auth/reset`, options)
        .catch(handleError)
        .then(handleFetchResponse)
        .then(({ json }) => {
            return json;
        });
};

export const completeNewPassword = params => {
    const { newPassword, ...extra } = params;
    return confirmSignIn({ challengeResponse: newPassword, options: extra });
};

export const forgotPassword = params => {
    const { username } = params;
    return Auth.resetPassword({ username });
};

export const resetPassword = params => {
    return Auth.confirmResetPassword(params);
};

export const signOut = ({ rememberMe = false, ...params } = {}) => {
    return API.get(API_NAME, '/logout').finally(() => {
        if (!rememberMe) {
            forgetMe();
        }
        AuthSymbol = undefined;
        Auth.signOut(params).catch(handleError);
    });
};

export const changePassword = async params => {
    const { newPassword, oldPassword, rememberMe, revoke } = params;
    const { username } = await getCognitoUser();
    const response = await Auth.updatePassword({ oldPassword, newPassword });
    if (!revoke) {
        return response;
    }
    await signOut({ global: true, rememberMe });
    return await signIn({ username, password: newPassword });
};
